import React, { FC, useMemo } from 'react';

import {
	BarDatum,
	EVINCED_SEVERITIES,
	EVINCED_SEVERITIES_IDS,
	EvSimpleBarChart,
	PatternData
} from '@evinced-private/ui-common';

import sumBy from 'lodash/sumBy';
import { IssueSeverities } from 'src/interfaces/IssuesOverviewResponse';

import { ColorsHelper } from '../../../helpers/charts/ColorsHelper';
import { getEvincedAdvantage } from '../../../helpers/ChartsHelper';
import { isValidationOfEvincedType } from '../../../helpers/ValidationHelper';

import './EvincedAdvantageBar.scss';

interface IEvincedAdvantageBarProps {
	severitiesData: IssueSeverities[];
	totalCriticalIssues: number;
}

const DEFAULT_CHART_HEIGHT = 250;
const DEFAULT_CHART_WIDTH = 400;

export const EvincedAdvantageBar: FC<IEvincedAdvantageBarProps> = ({
	severitiesData,
	totalCriticalIssues
}) => {
	const barData = useMemo((): BarDatum[] => {
		const criticalIssuesOverview = severitiesData.find(
			(issueSeverity) => issueSeverity.severity === EVINCED_SEVERITIES.CRITICAL
		);
		const nonEvincedCriticalIssues = criticalIssuesOverview.types.filter(
			({ type }) => !isValidationOfEvincedType(type)
		);
		const nonEvincedCriticalIssuesCount = sumBy(nonEvincedCriticalIssues, 'issuesCount');
		const advantage = getEvincedAdvantage(totalCriticalIssues, nonEvincedCriticalIssuesCount);

		return [
			{
				id: 'EVINCED',
				label: 'Evinced',
				count: totalCriticalIssues,
				advantage
			},
			{
				id: 'OTHER',
				label: 'Other tools',
				count: nonEvincedCriticalIssuesCount
			}
		];
	}, [totalCriticalIssues, severitiesData]);

	const patterns = useMemo(() => {
		return barData.map(
			(bar) =>
				({
					id: bar.id,
					label: bar.label,
					color: ColorsHelper.newSeverityColors[EVINCED_SEVERITIES_IDS.CRITICAL]
				}) as PatternData
		);
	}, [barData]);

	const customSrSummaryRenderer = (): JSX.Element => {
		const { advantage, count: evinced } = barData[0];
		const { count: others } = barData[1];
		const title = `Evinced has detected ${advantage} critical issues than other tools.`;
		return (
			<>
				{title}
				{`Evinced: ${evinced}`}
				{`OtherTools: ${others}`}
			</>
		);
	};

	return (
		<EvSimpleBarChart
			width={DEFAULT_CHART_WIDTH}
			height={DEFAULT_CHART_HEIGHT}
			title="Evinced advantage: auto detection of critical issues"
			data={barData}
			patterns={patterns}
			customSrSummaryRenderer={customSrSummaryRenderer}
			className="evinced-advantage"
			withLegend={false}
			withXAxis={false}
			withYAxis={false}
			withBarLabels={true}
			customTooltip={(context: { y: number }): string => {
				const { y } = context;
				return `${y} critical issues`;
			}}
			tableProps={{
				dataType: 'Tool',
				columnsPattern: [
					{ name: 'Technology', field: 'label' },
					{ name: 'Total Critical Issues', field: 'count' }
				]
			}}
		/>
	);
};
